import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, ErrorAlert } from '../components/kit.tsx';
import { command, query } from '../lib/api.ts';
import { usePageTitle } from '../lib/format.ts';

type Delivery = { state: 'pending' | 'sending' | 'sent' | 'retrying' | 'dead'; attempts: number; lastError: string | null };

/**
 * Delivery test of the Discord channels (Phase 3 exit gate: the staff channel receives a test
 * notification). Until train R1 the "students" webhook points at the staff test channel.
 */
export function AdminNotifications() {
  const { t } = useTranslation();
  usePageTitle(t('adminNotify.title'));
  const [sent, setSent] = useState<{ channel: string; outboxId: string } | null>(null);
  const send = useMutation({
    mutationFn: (channel: 'students' | 'ops') => command<{ outboxId: string }>('notify.testDiscord', { channel }),
    onSuccess: (r, channel) => setSent({ channel, outboxId: r.result.outboxId }),
  });
  const delivery = useQuery({
    queryKey: ['notify.delivery', sent?.outboxId],
    queryFn: () => query<Delivery>('notify.delivery', { outboxId: sent!.outboxId }),
    enabled: sent !== null,
    refetchInterval: (q) => (q.state.data?.state === 'sent' || q.state.data?.state === 'dead' ? false : 2000),
  });
  const state = delivery.data?.state;
  return (
    <div className="stack">
      <h1>{t('adminNotify.title')}</h1>
      <p>{t('adminNotify.intro')}</p>
      <ErrorAlert error={send.error ?? delivery.error} />
      <div className="row">
        <Button busy={send.isPending && send.variables === 'students'} onClick={() => send.mutate('students')}>{t('adminNotify.students')}</Button>
        <Button variant="secondary" busy={send.isPending && send.variables === 'ops'} onClick={() => send.mutate('ops')}>{t('adminNotify.ops')}</Button>
      </div>
      {sent ? (
        <p role="status" data-testid="delivery-state">
          {state === 'sent' ? t('adminNotify.delivered') : state === 'dead' ? t('adminNotify.failed', { error: delivery.data?.lastError ?? '' })
            : state === 'retrying' ? t('adminNotify.retrying', { error: delivery.data?.lastError ?? '' }) : t('adminNotify.queued')}
        </p>
      ) : null}
    </div>
  );
}
